/**
 * Zwei OnlyOffice-Einstiege: Kanzlei-Fläche und Akten-Fläche.
 * Dieselbe Session, Dokument-ID und derselbe Callback — getrennte Hülle.
 */

import { portalAkteIdFromNcPath, portalDokumentBelongsToAkte } from './portalAktePath';

export type PortalOnlyofficeEntry = 'kanzlei' | 'akte';

export type PortalOnlyofficeSurfaceDecision =
  | { ok: true; surface: 'kanzlei' | 'akte' }
  | { ok: false; surface: 'refuse'; reason: string };

export function decidePortalOnlyofficeSurface(input: {
  entry: PortalOnlyofficeEntry;
  documentAkteId?: string | null;
  openAkteId?: string | null;
}): PortalOnlyofficeSurfaceDecision {
  const documentAkteId = input.documentAkteId?.trim() ?? '';
  const openAkteId = input.openAkteId?.trim() ?? '';
  if (input.entry === 'kanzlei') {
    if (documentAkteId) {
      return {
        ok: false,
        surface: 'refuse',
        reason: 'Akten-Dokumente öffnen nur in der Akte.',
      };
    }
    return { ok: true, surface: 'kanzlei' };
  }
  if (!openAkteId || documentAkteId !== openAkteId) {
    return {
      ok: false,
      surface: 'refuse',
      reason: 'Dieses Dokument gehört nicht zu dieser Akte.',
    };
  }
  return { ok: true, surface: 'akte' };
}

export type AkteOfficePlusDoc = {
  akte_id?: string | null;
  nextcloud_path?: string | null;
};

/** Plus im Akten-OnlyOffice: nur diese Akte öffnen, Pool nur zum Hinzufügen, fremde Akte nie. */
export function akteOnlyofficePlusKind(
  doc: AkteOfficePlusDoc,
  akte: { id: string; nc_path?: string | null },
): 'this-akte' | 'pool' | 'refuse' {
  const akteId = akte.id?.trim() ?? '';
  if (!akteId) return 'refuse';
  if (portalDokumentBelongsToAkte(doc, { id: akteId, nc_path: akte.nc_path })) return 'this-akte';
  if ((doc.akte_id || '').trim()) return 'refuse';
  if (portalAkteIdFromNcPath(doc.nextcloud_path)) return 'refuse';
  return 'pool';
}

/** Seitenzahl nur für PDF. DOCX hat hier keinen bestehenden Seitenweg. */
export function portalAkteFirstPageMode(filename?: string | null): 'pdf' | 'text' {
  return portalFileTypeMark(filename).tone === 'pdf' ? 'pdf' : 'text';
}

export function portalFileTypeMark(filename?: string | null): { label: string; tone: 'pdf' | 'docx' | 'plain' } {
  const base = (filename || '').trim().split(/[/\\]/).pop() || '';
  const dot = base.lastIndexOf('.');
  const ext = dot >= 0 ? base.slice(dot + 1).toLowerCase() : '';
  if (ext === 'pdf') return { label: 'PDF', tone: 'pdf' };
  if (ext === 'docx') return { label: 'DOCX', tone: 'docx' };
  if (!ext) return { label: 'Datei', tone: 'plain' };
  return { label: ext.slice(0, 5).toUpperCase(), tone: 'plain' };
}

export function filterDocsByFilename<T extends { filename?: string | null }>(
  docs: readonly T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...docs];
  return docs.filter((doc) => (doc.filename || '').toLowerCase().includes(needle));
}
